/**
 * One language's strings as a JSON file: what the export writes and the
 * import reads. A flat object, key to text, sorted, so two exports of the
 * same strings are the same file and a diff between two shows what changed.
 *
 * Only the SHAPE is checked here. Which keys are allowed, how long a value
 * may be and how many one write may carry are the worker's rules
 * (`saveTranslations`), and it answers an import that breaks one in words.
 */
export type Strings = Record<string, string>;

export function fileName(namespace: string, locale: string): string {
  return `${namespace}.${locale}.json`;
}

export function toFile(strings: Strings): string {
  const sorted: Strings = {};
  for (const key of Object.keys(strings).sort()) sorted[key] = strings[key]!;
  return `${JSON.stringify(sorted, null, 2)}\n`;
}

export type ReadFile = { ok: true; strings: Strings } | { ok: false; message: string };

export function readFile(text: string): ReadFile {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, message: "That file is not JSON." };
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return { ok: false, message: "That file is not an object of keys and their text." };
  }
  const entries: [string, string][] = [];
  for (const [key, value] of Object.entries(parsed)) {
    if (typeof value !== "string") {
      return { ok: false, message: `The value for ${key.slice(0, 60)} is not text.` };
    }
    entries.push([key, value]);
  }
  // `fromEntries` defines each key as its own property, so a `__proto__` in
  // the file is a key like any other (the worker refuses it by name).
  return { ok: true, strings: Object.fromEntries(entries) };
}

/** What an import would do: only new and changed keys are written. */
export interface ImportPlan {
  added: string[];
  changed: string[];
  same: number;
  /** New and changed, the keys the write carries. */
  writes: Strings;
}

export function planImport(current: Strings, incoming: Strings): ImportPlan {
  // Pairs, then `fromEntries`: a `__proto__` key stays a key (see
  // `readFile`), where `writes[key] = value` would set the prototype.
  const writes: [string, string][] = [];
  const counts: Omit<ImportPlan, "writes"> = { added: [], changed: [], same: 0 };
  for (const [key, value] of Object.entries(incoming)) {
    if (!Object.prototype.hasOwnProperty.call(current, key)) counts.added.push(key);
    else if (current[key] !== value) counts.changed.push(key);
    else {
      counts.same += 1;
      continue;
    }
    writes.push([key, value]);
  }
  return { ...counts, writes: Object.fromEntries(writes) };
}
